/**
 * SyncStatusBadge - Displays sync status for portfolios
 * TASK-321/TASK-324: Reusable badge for showing data source and sync status
 *
 * Shows colored status indicator based on sync state with optional details.
 * TASK-661: colors come from the semantic status tokens (success/attention/
 * urgent/info/unavailable; external for non-manual sources) — this composite
 * badge shares the product-wide status anatomy and never encodes state by
 * color alone (icon + label always present).
 */

import React from 'react';
import {
  CheckCircle2,
  AlertCircle,
  XCircle,
  RefreshCw,
  Clock,
  CloudOff,
  Database,
  HelpCircle,
  Upload
} from 'lucide-react';

export type SyncStatus = 'success' | 'partial' | 'failed' | 'pending' | 'running' | 'never';
export type DataSource = 'manual' | 'pretim' | 'csv_import';

export interface SyncStatusBadgeProps {
  status: SyncStatus;
  dataSource: DataSource;
  lastSyncAt?: string;
  syncEnabled?: boolean;
  showDetails?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SyncStatusBadge: React.FC<SyncStatusBadgeProps> = ({
  status,
  dataSource,
  lastSyncAt,
  syncEnabled = false,
  showDetails = false,
  size = 'md',
  className = ''
}) => {
  // Status configuration
  const statusConfig: Record<SyncStatus, {
    icon: React.ElementType;
    color: string;
    bg: string;
    border: string;
    label: string;
    animate?: boolean;
  }> = {
    success: {
      icon: CheckCircle2,
      color: 'text-status-success-fg',
      bg: 'bg-status-success/10',
      border: 'border-status-success/30',
      label: 'Synced'
    },
    partial: {
      icon: AlertCircle,
      color: 'text-status-attention-fg',
      bg: 'bg-status-attention/10',
      border: 'border-status-attention/30',
      label: 'Partial'
    },
    failed: {
      icon: XCircle,
      color: 'text-status-urgent-fg',
      bg: 'bg-status-urgent/10',
      border: 'border-status-urgent/30',
      label: 'Failed'
    },
    pending: {
      icon: Clock,
      color: 'text-status-info-fg',
      bg: 'bg-status-info/10',
      border: 'border-status-info/30',
      label: 'Pending'
    },
    running: {
      icon: RefreshCw,
      color: 'text-status-info-fg',
      bg: 'bg-status-info/10',
      border: 'border-status-info/30',
      label: 'Syncing',
      animate: true
    },
    never: {
      icon: CloudOff,
      color: 'text-status-unavailable-fg',
      bg: 'bg-status-unavailable/10',
      border: 'border-status-unavailable/30',
      label: 'Never synced'
    }
  };

  // Data source configuration
  const dataSourceConfig: Record<DataSource, {
    icon: React.ElementType;
    label: string;
    color: string;
  }> = {
    manual: {
      icon: Database,
      label: 'Manual',
      color: 'text-muted-foreground'
    },
    // Non-manual sources are externally sourced data — the `external` token.
    pretim: {
      icon: RefreshCw,
      label: 'Pretim',
      color: 'text-status-external'
    },
    csv_import: {
      icon: Upload,
      label: 'CSV Import',
      color: 'text-status-external'
    }
  };

  // Size configuration
  const sizeConfig = {
    sm: {
      badge: 'px-2 py-0.5 text-xs',
      icon: 'w-3 h-3',
      gap: 'gap-1'
    },
    md: {
      badge: 'px-2.5 py-1 text-sm',
      icon: 'w-3.5 h-3.5',
      gap: 'gap-1.5'
    },
    lg: {
      badge: 'px-3 py-1.5 text-base',
      icon: 'w-4 h-4',
      gap: 'gap-2'
    }
  };

  // Guard at the lookup (TASK-602): a status/source value outside the union used
  // to crash every consumer with "Cannot read properties of undefined". Unknown
  // values render an honest "unknown" badge instead.
  const UNKNOWN_STATUS = {
    icon: HelpCircle,
    label: 'Unknown',
    color: 'text-status-unavailable-fg',
    bg: 'bg-status-unavailable/10',
    border: 'border-status-unavailable/30',
  };
  const statusInfo = statusConfig[status] ?? UNKNOWN_STATUS;
  const sourceInfo = dataSourceConfig[dataSource] ?? dataSourceConfig.manual;
  const sizeInfo = sizeConfig[size] ?? sizeConfig.md;

  // Format last sync time
  const formatLastSync = (dateString?: string) => {
    if (!dateString) return null;

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  const StatusIcon = statusInfo.icon;
  const SourceIcon = sourceInfo.icon;

  // For manual data source with no sync, show simple badge
  if (dataSource === 'manual' && !syncEnabled) {
    return (
      <span className={`
        inline-flex items-center ${sizeInfo.gap} ${sizeInfo.badge}
        bg-muted text-muted-foreground border border-border
        font-medium rounded-full
        ${className}
      `}>
        <SourceIcon className={sizeInfo.icon} />
        Manual
      </span>
    );
  }

  return (
    <div className={`inline-flex flex-col ${className}`}>
      {/* Main badge */}
      <span
        role="status"
        aria-label={`Sync status: ${statusInfo.label} (${sourceInfo.label})`}
        className={`
        inline-flex items-center ${sizeInfo.gap} ${sizeInfo.badge}
        ${statusInfo.bg} ${statusInfo.color} ${statusInfo.border}
        font-medium rounded-full border
      `}>
        <StatusIcon className={`${sizeInfo.icon} ${statusInfo.animate ? 'animate-spin' : ''}`} />
        <span>{statusInfo.label}</span>
        {dataSource !== 'manual' && (
          <>
            <span className="text-muted-foreground/60">|</span>
            <SourceIcon className={`${sizeInfo.icon} ${sourceInfo.color}`} />
          </>
        )}
      </span>

      {/* Details row */}
      {showDetails && lastSyncAt && status !== 'never' && (
        <span className={`
          text-xs text-gray-500 mt-0.5
          ${size === 'sm' ? 'text-[10px]' : ''}
        `}>
          Last: {formatLastSync(lastSyncAt)}
        </span>
      )}
    </div>
  );
};

export default SyncStatusBadge;
